import { supabase } from "../lib/supabase";

export interface SitemapUpdateResult {
  success: boolean;
  message: string;
  instructions?: string[];
  nextSteps?: string[];
}

export class SitemapService {
  /**
   * Trigger sitemap regeneration with detailed instructions
   */
  static async triggerSitemapRegeneration(): Promise<SitemapUpdateResult> {
    try {
      console.log("🔄 Sitemap regeneration triggered from SEO optimization");

      // Get the latest blog posts to include in sitemap (exclude deleted posts)
      const { data: posts, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, updated_at, seo_optimized_at")
        .eq("is_published", true)
        .eq("deleted", false)
        .order("updated_at", { ascending: false });

      if (error) {
        console.error("Error fetching posts for sitemap:", error);
        return {
          success: false,
          message: "Failed to fetch posts for sitemap generation",
        };
      }

      const optimizedPosts =
        posts?.filter((post) => post.seo_optimized_at) || [];
      const totalPosts = posts?.length || 0;

      console.log(
        `📊 Sitemap will include ${totalPosts} posts (${optimizedPosts.length} SEO optimized)`,
      );

      return {
        success: true,
        message: "Sitemap regeneration triggered successfully",
        instructions: [
          "1. Run: npm run generate-enhanced-sitemap",
          "2. The sitemap will be generated in the dist/ directory",
          "3. Upload the new sitemap to your hosting provider",
          "4. Submit the sitemap URL to Google Search Console",
        ],
        nextSteps: [
          `Include ${totalPosts} published posts in sitemap`,
          `Highlight ${optimizedPosts.length} SEO-optimized posts`,
          "Generate image sitemap for better image SEO",
          "Create sitemap index for large sites",
        ],
      };
    } catch (error) {
      console.error("Error triggering sitemap regeneration:", error);
      return {
        success: false,
        message: "Failed to trigger sitemap regeneration",
      };
    }
  }

  /**
   * Get sitemap statistics
   */
  static async getSitemapStats(): Promise<{
    totalPosts: number;
    optimizedPosts: number;
    recentPosts: number;
    lastOptimized: string | null;
    totalPrompts: number;
    totalPages: number;
    seoPagesOptimized: number;
  }> {
    try {
      // Fetch blog posts
      const { data: posts, error: postsError } = await supabase
        .from("blog_posts")
        .select("id, title, slug, updated_at, seo_optimized_at")
        .eq("is_published", true)
        .eq("deleted", false)
        .order("updated_at", { ascending: false });

      if (postsError) {
        console.error("Error fetching blog posts:", postsError);
      }

      // Fetch prompts
      const { data: prompts, error: promptsError } = await supabase
        .from("prompts")
        .select("id, title, slug, updated_at")
        .eq("is_published", true)
        .eq("deleted", false)
        .order("updated_at", { ascending: false });

      if (promptsError) {
        console.error("Error fetching prompts:", promptsError);
      }

      // Calculate post statistics (blog posts only)
      const optimizedPosts =
        posts?.filter((post) => post.seo_optimized_at) || [];
      const recentPosts =
        posts?.filter((post) => {
          const postDate = new Date(post.updated_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return postDate > thirtyDaysAgo;
        }) || [];

      // Define static pages (not posts or prompts) with their actual SEO status
      const staticPages = [
        {
          title: "Landing Page",
          slug: "/",
          seo_optimized_at: "2025-01-07T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Original optimization
        {
          title: "Courses",
          slug: "/courses",
          seo_optimized_at: "2025-01-07T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Original optimization
        {
          title: "Fractional PM",
          slug: "/fractional-pm",
          seo_optimized_at: "2025-01-07T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Original optimization
        {
          title: "Blog",
          slug: "/blog",
          seo_optimized_at: "2025-08-08T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Recently optimized
        {
          title: "About",
          slug: "/about",
          seo_optimized_at: "2025-01-07T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Original optimization
        {
          title: "Prompt Library",
          slug: "/prompt-library",
          seo_optimized_at: "2025-01-07T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Original optimization
        {
          title: "Contact",
          slug: "/contact",
          seo_optimized_at: "2025-08-08T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Recently optimized
        {
          title: "Privacy Policy",
          slug: "/privacy",
          seo_optimized_at: "2025-08-08T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Recently optimized
        {
          title: "Terms of Service",
          slug: "/terms",
          seo_optimized_at: "2025-08-08T00:00:00.000Z",
          updated_at: "2025-08-08T00:00:00.000Z",
        }, // ✅ Recently optimized
      ];

      // Calculate page statistics (static pages only)
      const totalPages = staticPages.length; // 9 static pages
      const seoPagesOptimized = staticPages.filter(
        (page) => page.seo_optimized_at,
      ).length; // 9 optimized pages (all pages are now optimized)

      const lastOptimized =
        optimizedPosts.length > 0 ? optimizedPosts[0].seo_optimized_at : null;

      return {
        totalPosts: posts?.length || 0, // Blog posts only
        optimizedPosts: optimizedPosts.length, // Optimized blog posts only
        recentPosts: recentPosts.length, // Recent blog posts only
        lastOptimized,
        totalPrompts: prompts?.length || 0, // Prompts only
        totalPages, // Static pages only (9)
        seoPagesOptimized, // Optimized static pages only (5)
      };
    } catch (error) {
      console.error("Error getting sitemap stats:", error);
      return {
        totalPosts: 0,
        optimizedPosts: 0,
        recentPosts: 0,
        lastOptimized: null,
        totalPrompts: 0,
        totalPages: 0,
        seoPagesOptimized: 0,
      };
    }
  }

  /**
   * Check if sitemap needs regeneration
   */
  static async checkSitemapStatus(): Promise<{
    needsRegeneration: boolean;
    reason: string;
    lastUpdate: string | null;
  }> {
    try {
      const stats = await this.getSitemapStats();

      // Check if there are new posts or recent SEO optimizations
      const needsRegeneration =
        stats.recentPosts > 0 ||
        (stats.lastOptimized &&
          new Date(stats.lastOptimized) >
            new Date(Date.now() - 24 * 60 * 60 * 1000)); // Last 24 hours

      return {
        needsRegeneration,
        reason: needsRegeneration
          ? `Recent activity: ${stats.recentPosts} new posts, ${stats.optimizedPosts} SEO optimized`
          : "No recent changes detected",
        lastUpdate: stats.lastOptimized,
      };
    } catch (error) {
      console.error("Error checking sitemap status:", error);
      return {
        needsRegeneration: false,
        reason: "Error checking status",
        lastUpdate: null,
      };
    }
  }

  /**
   * Get detailed content data for modal popups
   */
  static async getDetailedContentData(): Promise<{
    posts: any[];
    prompts: any[];
  }> {
    try {
      // Fetch blog posts
      const { data: posts, error: postsError } = await supabase
        .from("blog_posts")
        .select("id, title, slug, updated_at, seo_optimized_at")
        .eq("is_published", true)
        .eq("deleted", false)
        .order("updated_at", { ascending: false });

      if (postsError) {
        console.error("Error fetching blog posts:", postsError);
      }

      // Fetch prompts
      const { data: prompts, error: promptsError } = await supabase
        .from("prompts")
        .select("id, title, slug, updated_at")
        .eq("is_published", true)
        .eq("deleted", false)
        .order("updated_at", { ascending: false });

      if (promptsError) {
        console.error("Error fetching prompts:", promptsError);
      }

      return {
        posts: posts || [],
        prompts: prompts || [],
      };
    } catch (error) {
      console.error("Error getting detailed content data:", error);
      return {
        posts: [],
        prompts: [],
      };
    }
  }
}
